import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container,
  Typography,
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  TextField,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
  IconButton,
} from '@mui/material';
import {
  Visibility,
  CheckCircle,
  Cancel,
  Search,
} from '@mui/icons-material';
import { useDispatch, useSelector } from 'react-redux';
import { fetchBookings } from '../../store/slices/bookingSlice';
import bookingService from '../../services/api/bookingService';
import { Alert } from '@mui/material';
import { BookingStatusChip } from '../../utils/bookingStatus';
import { resolveBookingPackageLabel } from '../../utils/bookingPackageLabel';

const BookingsPage = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { bookings = [], loading, error } = useSelector((state) => state.bookings);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('');

  useEffect(() => {
    dispatch(fetchBookings({ search: searchTerm, status: statusFilter }));
  }, [dispatch, searchTerm, statusFilter]);

  const formatAmount = (amount, currency) => {
    const num = Number(amount || 0).toLocaleString();
    if (currency === 'PKR') return `PKR ${num}`;
    return `$${num} ${currency || 'USD'}`;
  };

  const handleConfirm = async (bookingId) => {
    try {
      await bookingService.confirmBooking(bookingId);
      dispatch(fetchBookings({ search: searchTerm, status: statusFilter }));
    } catch (error) {
      console.error('Error confirming booking:', error);
    }
  };

  const handleCancel = async (bookingId) => {
    const reason = window.prompt('Cancellation reason (required):', 'Cancelled by admin');
    if (!reason?.trim()) return;
    try {
      await bookingService.cancelBooking(bookingId, reason.trim());
      dispatch(fetchBookings({ search: searchTerm, status: statusFilter }));
    } catch (error) {
      console.error('Error cancelling booking:', error);
      window.alert(error?.response?.data?.message || 'Failed to cancel booking');
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString();
  };

  return (
    <Container maxWidth="xl">
      <Typography variant="h4" component="h1" gutterBottom>
        Bookings Management
      </Typography>

      {/* Filters */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Grid container spacing={2}>
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              label="Search by Reference"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: <Search sx={{ mr: 1, color: 'text.secondary' }} />,
              }}
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <FormControl fullWidth>
              <InputLabel>Status</InputLabel>
              <Select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                label="Status"
              >
                <MenuItem value="">All</MenuItem>
                <MenuItem value="Pending">Pending</MenuItem>
                <MenuItem value="Confirmed">Confirmed</MenuItem>
                <MenuItem value="Cancelled">Cancelled</MenuItem>
              </Select>
            </FormControl>
          </Grid>
        </Grid>
      </Paper>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {typeof error === 'string' ? error : (error.message || 'Failed to load bookings from server.')}
        </Alert>
      )}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress />
        </Box>
      ) : (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Reference</TableCell>
                <TableCell>Package</TableCell>
                <TableCell>Customer</TableCell>
                <TableCell>Travel Dates</TableCell>
                <TableCell>Travelers</TableCell>
                <TableCell>Amount</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Booking Date</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {bookings.map((booking) => (
                <TableRow key={booking.id}>
                  <TableCell>
                    <Typography variant="body2" fontWeight="bold">
                      {booking.bookingReference}
                    </Typography>
                  </TableCell>
                  <TableCell>{resolveBookingPackageLabel(booking)}</TableCell>
                  <TableCell>
                    {booking.customerName ||
                      (booking.guestFirstName ? `${booking.guestFirstName} ${booking.guestLastName || ''}`.trim() : '') ||
                      booking.customizedData?.yourName ||
                      booking.userId ||
                      'N/A'}
                  </TableCell>
                  <TableCell>
                    {formatDate(booking.travelStartDate)} - {formatDate(booking.travelEndDate)}
                  </TableCell>
                  <TableCell>{booking.numberOfTravelers}</TableCell>
                  <TableCell>
                    <Typography variant="body2" fontWeight="bold">
                      {formatAmount(booking.totalAmount, booking.currency)}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <BookingStatusChip status={booking.status} />
                  </TableCell>
                  <TableCell>{formatDate(booking.bookingDate)}</TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', gap: 1 }}>
                      <IconButton
                        size="small"
                        onClick={() => navigate(`/bookings/${booking.id}`)}
                      >
                        <Visibility />
                      </IconButton>
                      {booking.status?.toLowerCase() === 'pending' && (
                        <>
                          <IconButton
                            size="small"
                            color="success"
                            onClick={() => handleConfirm(booking.id)}
                          >
                            <CheckCircle />
                          </IconButton>
                          <IconButton
                            size="small"
                            color="error"
                            title="Cancel booking"
                            onClick={() => handleCancel(booking.id)}
                          >
                            <Cancel />
                          </IconButton>
                        </>
                      )}
                    </Box>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Container>
  );
};

export default BookingsPage;
